import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, {
  CheckUserExpiry,
  getUserDetails,
  isFisherOwnerUser,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { formatDisplayDate } from "../../utils/formatDisplayDate";

function memberInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "S";
}

function inviteStatusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "accepted") return "natural";
  if (s === "pending") return "river";
  if (s === "declined" || s === "cancelled" || s === "canceled") return "other";
  return "other";
}

function inviteStatusLabel(status) {
  const s = String(status || "").trim();
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const InviteScientist = () => {
  const fisherID = getUserDetails()?.FisherID;
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loadInvites = useCallback(async () => {
    if (!fisherID) return;
    try {
      const { data } = await axiosInstance.get(`api/User/FisherScientistInvites/${fisherID}`);
      setPendingInvites(Array.isArray(data) ? data : []);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load invitations");
    }
  }, [fisherID]);

  useEffect(() => {
    if (!isFisherOwnerUser()) {
      setIsLoading(false);
      return;
    }
    (async () => {
      await CheckUserExpiry();
      await loadInvites();
      setIsLoading(false);
    })();
  }, [loadInvites]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      showErrorToast("Enter an email address");
      return;
    }
    setIsSearching(true);
    setSearchResult(null);
    try {
      const { data } = await axiosInstance.get("api/User/SearchIndependentScientists", {
        params: { email: trimmed, fisherID },
      });
      setSearchResult(data);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendInvite = async () => {
    if (!searchResult?.found || !searchResult.scientist) return;
    setIsSending(true);
    try {
      await axiosInstance.post("api/User/SendScientistInvite", {
        FisherID: fisherID,
        StaffID: searchResult.scientist.id,
        Email: searchResult.scientist.Email,
        Message: message.trim() || undefined,
      });
      showSuccessToast("Invitation sent");
      setMessage("");
      setSearchResult(null);
      setEmail("");
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to send invitation");
    } finally {
      setIsSending(false);
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await axiosInstance.post(`api/User/CancelScientistInvite/${inviteId}`);
      showSuccessToast("Invitation cancelled");
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to cancel invitation");
    }
  };

  if (isLoading) return <Preloader />;

  if (!isFisherOwnerUser()) {
    return (
      <div className="donezo-page donezo-list-page">
        <div className="container-fluid px-3 px-md-4 py-5">
          <div className="donezo-empty-state mx-auto" style={{ maxWidth: 440 }}>
            <div className="donezo-empty-state__icon" aria-hidden>
              <i className="fa fa-lock" />
            </div>
            <p className="donezo-empty-state__title">Owner access required</p>
            <p className="donezo-empty-state__text">
              Only organization owners can invite independent scientists.
            </p>
            <Link to="/staff" className="btn btn-primary rounded-pill px-4 mt-3">
              Back to team
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-upload-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar donezo-upload-toolbar">
          <div className="donezo-upload-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">Invite scientist</h1>
            <p className="donezo-upload-toolbar__subtitle">
              Search by the exact email they used to register. They must accept before you can assign
              ponds.
            </p>
          </div>
          <div className="donezo-orders-toolbar__actions">
            <Link to="/staff" className="donezo-orders-toolbar__ghost">
              <i className="fa fa-arrow-left" aria-hidden />
              <span>Back to team</span>
            </Link>
          </div>
        </div>

        <div className="donezo-orders-panel donezo-upload-section mb-3">
          <div className="donezo-upload-section__header">
            <i className="fa fa-search" aria-hidden />
            <h2 className="donezo-upload-section__title">Find scientist</h2>
          </div>
          <div className="donezo-upload-section__body">
            <form onSubmit={handleSearch} className="donezo-invite-search">
              <label htmlFor="invite-scientist-email" className="visually-hidden">
                Scientist email
              </label>
              <div className="donezo-orders-toolbar__search donezo-invite-search__field">
                <i className="fa fa-envelope" aria-hidden />
                <input
                  id="invite-scientist-email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="scientist@example.com"
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                className="donezo-orders-toolbar__primary"
                disabled={isSearching}
              >
                {isSearching ? "Searching…" : "Search"}
              </button>
            </form>

            {searchResult && !searchResult.found && (
              <div className="donezo-upload-notice donezo-upload-notice--warn mt-3 mb-0">
                No independent scientist account found. Ask them to register at Sign Up → Independent
                Scientist.
              </div>
            )}

            {searchResult?.found && searchResult.scientist && (
              <div className="donezo-invite-result mt-3">
                <div className="donezo-orders-pond">
                  <span className="donezo-orders-pond__avatar" aria-hidden>
                    {memberInitial(searchResult.scientist.FullName)}
                  </span>
                  <div className="donezo-orders-pond__meta">
                    <span className="donezo-orders-pond__name">
                      {searchResult.scientist.FullName}
                    </span>
                    <span className="donezo-team-email" title={searchResult.scientist.Email}>
                      {searchResult.scientist.Email}
                    </span>
                  </div>
                </div>

                <label htmlFor="invite-message" className="form-label small fw-medium mt-3 mb-1">
                  Optional message
                </label>
                <textarea
                  id="invite-message"
                  className="form-control"
                  rows={2}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Brief note for the scientist"
                />

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <button
                    type="button"
                    className="donezo-orders-toolbar__primary"
                    onClick={handleSendInvite}
                    disabled={isSending}
                  >
                    {isSending ? "Sending…" : "Send invitation"}
                  </button>
                  <button
                    type="button"
                    className="donezo-orders-toolbar__ghost"
                    onClick={() => {
                      setSearchResult(null);
                      setMessage("");
                    }}
                    disabled={isSending}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="donezo-orders-panel donezo-upload-section">
          <div className="donezo-upload-section__header donezo-upload-section__header--split">
            <div className="d-flex align-items-center gap-2">
              <i className="fa fa-inbox" aria-hidden />
              <h2 className="donezo-upload-section__title">Invitations</h2>
            </div>
            <span className="donezo-upload-section__meta">
              {pendingInvites.length} {pendingInvites.length === 1 ? "entry" : "entries"}
            </span>
          </div>

          {pendingInvites.length === 0 ? (
            <div className="donezo-empty-state donezo-empty-state--compact">
              <div className="donezo-empty-state__icon" aria-hidden>
                <i className="fa fa-paper-plane" />
              </div>
              <p className="donezo-empty-state__title">No outgoing invitations</p>
              <p className="donezo-empty-state__text">
                Search for a scientist above to send your first invite.
              </p>
            </div>
          ) : (
            <>
              <div className="d-none d-md-block table-responsive">
                <table className="table align-middle mb-0 donezo-orders-table">
                  <thead>
                    <tr>
                      <th>Scientist</th>
                      <th>Status</th>
                      <th>Sent</th>
                      <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvites.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          <div className="donezo-orders-pond">
                            <span className="donezo-orders-pond__avatar" aria-hidden>
                              {memberInitial(inv.Scientist?.FullName)}
                            </span>
                            <div className="donezo-orders-pond__meta">
                              <span className="donezo-orders-pond__name">
                                {inv.Scientist?.FullName || "—"}
                              </span>
                              <span
                                className="donezo-team-email"
                                title={inv.Scientist?.Email || undefined}
                              >
                                {inv.Scientist?.Email || "—"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`donezo-status-dot donezo-status-dot--${inviteStatusTone(
                              inv.Status
                            )}`}
                          >
                            <span className="donezo-status-dot__mark" aria-hidden />
                            {inviteStatusLabel(inv.Status)}
                          </span>
                        </td>
                        <td className="donezo-orders-table__date">
                          {inv.createdAt ? formatDisplayDate(inv.createdAt) : "—"}
                        </td>
                        <td className="donezo-orders-table__actions-col">
                          {inv.Status === "pending" ? (
                            <div className="donezo-orders-actions is-visible">
                              <button
                                type="button"
                                className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
                                title="Cancel invitation"
                                aria-label="Cancel invitation"
                                onClick={() => cancelInvite(inv.id)}
                              >
                                <i className="fa fa-times" aria-hidden />
                              </button>
                            </div>
                          ) : (
                            <span className="donezo-orders-table__date">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-md-none donezo-orders-panel__mobile">
                {pendingInvites.map((inv) => (
                  <div key={inv.id} className="donezo-mobile-card">
                    <div className="donezo-mobile-card__head">
                      <div className="donezo-orders-pond">
                        <span className="donezo-orders-pond__avatar" aria-hidden>
                          {memberInitial(inv.Scientist?.FullName)}
                        </span>
                        <div className="donezo-orders-pond__meta">
                          <h2 className="donezo-mobile-card__title mb-0">
                            {inv.Scientist?.FullName || "—"}
                          </h2>
                          <span className="donezo-team-email">{inv.Scientist?.Email || "—"}</span>
                        </div>
                      </div>
                      {inv.Status === "pending" && (
                        <div className="donezo-orders-actions is-visible">
                          <button
                            type="button"
                            className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
                            title="Cancel invitation"
                            aria-label="Cancel invitation"
                            onClick={() => cancelInvite(inv.id)}
                          >
                            <i className="fa fa-times" aria-hidden />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="donezo-mobile-card__grid">
                      <div>
                        <span className="donezo-mobile-card__label">Status</span>
                        <div className="donezo-mobile-card__value">
                          <span
                            className={`donezo-status-dot donezo-status-dot--${inviteStatusTone(
                              inv.Status
                            )}`}
                          >
                            <span className="donezo-status-dot__mark" aria-hidden />
                            {inviteStatusLabel(inv.Status)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="donezo-mobile-card__label">Sent</span>
                        <div className="donezo-mobile-card__value donezo-orders-table__date">
                          {inv.createdAt ? formatDisplayDate(inv.createdAt) : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteScientist;
